import { useEffect, useState } from "react";

import { ExternalLink, Loader2, MapPin } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import type { NearestGeodata } from "@/pages/geodata/lib/nearest-geodata";
import { readCityCenter } from "@/pages/tina/items/components/panels/hitl-panel/city-center";
import { CoalescedResolveBanner } from "@/pages/tina/items/components/panels/hitl-panel/coalescing";
import { readMusicEventContext } from "@/pages/tina/items/components/panels/hitl-panel/hitl-readers";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useSiteCity } from "@/pages/tina/items/components/panels/hitl-panel/use-site-city";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import type { HitlResolutionInput } from "@/types/tina";

import {
  cityMapsUrl,
  isGoogleMapsUrl,
  readCandidateCoords,
} from "./venue-resolution.helpers";

type Mode = "candidate" | "pick" | "maps" | "use_maps";

interface VenueOption {
  id: string;
  name: string;
  address?: string;
  media?: { photos?: Array<{ url: string }> };
}

interface GeodataOption {
  id: string;
  name: string;
  countryCode?: string;
  region?: string;
  type?: string;
}

interface PlacesCandidate {
  name: string;
  address?: string;
  cid?: string;
  mapsUrl?: string;
  inDb: boolean;
  dbVenueId?: string;
}

function readPlacesCandidates(
  problem: Record<string, unknown>,
): PlacesCandidate[] {
  const raw = Array.isArray(problem.places_candidates)
    ? problem.places_candidates
    : [];
  return raw.flatMap((entry) => {
    const c = (entry ?? {}) as Record<string, unknown>;
    const name = typeof c.name === "string" ? c.name : "";
    if (!name) return [];
    const cid = typeof c.cid === "string" ? c.cid : undefined;
    const mapsUrl =
      typeof c.google_maps_uri === "string" && c.google_maps_uri
        ? c.google_maps_uri
        : cid
          ? `https://maps.google.com/?cid=${cid}`
          : undefined;
    return [
      {
        name,
        address:
          typeof c.formatted_address === "string"
            ? c.formatted_address
            : undefined,
        cid,
        mapsUrl,
        inDb: c.in_db_already === true,
        dbVenueId:
          typeof c.db_venue_id === "string" ? c.db_venue_id : undefined,
      },
    ];
  });
}

function formatNearestGeodataLabel(g: NearestGeodata): string {
  const region = g.region ? `, ${g.region}` : "";
  const country = g.countryCode ? ` (${g.countryCode})` : "";
  return `${g.name}${region}${country} · ~${Math.round(g.distanceKm)} km away`;
}

function MapsUrlWarning() {
  return (
    <span className="text-xs font-medium text-destructive">
      That&apos;s not a Google Maps link. Paste a maps.app.goo.gl short link or
      a google.com/maps URL.
    </span>
  );
}

export function VenueResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const { search } = useVenueRepository();
  const {
    search: searchGeodata,
    searchNearest,
    get: getGeodata,
  } = useGeodataRepository();

  const problem = hitlContext.problem as unknown as Record<string, unknown>;
  const isGeodataMiss = hitlContext.reason === "venue_geodata_miss";
  const selectedCandidateFull =
    isGeodataMiss &&
    problem.selected_candidate_full &&
    typeof problem.selected_candidate_full === "object"
      ? (problem.selected_candidate_full as Record<string, unknown>)
      : null;
  const candidateName =
    selectedCandidateFull && typeof selectedCandidateFull.name === "string"
      ? selectedCandidateFull.name
      : null;
  const extractedName =
    typeof problem.extracted_venue_name === "string"
      ? problem.extracted_venue_name
      : null;
  const candidates = readPlacesCandidates(problem);
  // For a geodata miss the chosen venue candidate carries coordinates; use them
  // to offer the closest cities as quick picks (skipped when coords are absent).
  const candidateCoords = isGeodataMiss ? readCandidateCoords(problem) : null;
  // The backend stashes the resolved maps link when a maps-based resolution
  // hits a geodata miss and is re-HITL'd, so the curator can re-resolve the
  // same link after picking geodata without re-pasting it.
  const prefilledMapsUrl =
    typeof problem.maps_url === "string" ? problem.maps_url : "";
  const isMusicEvent = item.content_type === "music_event";
  // The city behind the "use this city's center" button. The event's own city comes
  // first and is almost never there on a venue HITL — see `useSiteCity` for why —
  // so the site it was scraped from is the fallback that makes the button appear.
  const eventCity = readMusicEventContext(item, hitlContext)?.cityName;
  const siteCity = useSiteCity(
    isMusicEvent && !eventCity ? item.site : undefined,
  );
  const cityName = eventCity ?? siteCity;

  const [mode, setMode] = useState<Mode>(() => {
    if (isGeodataMiss && candidateName) return "candidate";
    // A geodata miss carrying only a maps link came from a use_maps_url
    // resolution (location-only) — preserve that intent on the retry.
    if (isGeodataMiss && prefilledMapsUrl && isMusicEvent) return "use_maps";
    return "pick";
  });
  const [venue, setVenue] = useState<VenueOption | undefined>();
  const [geodata, setGeodata] = useState<GeodataOption | undefined>();
  const [mapsUrl, setMapsUrl] = useState(prefilledMapsUrl);
  const [submitting, setSubmitting] = useState(false);
  const [nearestGeodata, setNearestGeodata] = useState<NearestGeodata[]>([]);
  const [autofillingCity, setAutofillingCity] = useState(false);

  // TBA-venue helper: the curator's usual fallback for "no venue yet" is to
  // paste the city's own Google Maps link, so offer to do that lookup for
  // them from the event's own city instead of a curator round-trip to Maps.
  //
  // Two calls, not one. `GET /geodata/explore` answers with `GeodataSummary`, which
  // carries no coordinates at all — reading them off the search hit set the city and
  // left the link box empty, which is how this shipped broken. `readCityCenter` does
  // the single-record read that does carry them.
  async function handleAutofillCity() {
    if (!cityName) return;
    setAutofillingCity(true);
    try {
      const center = await readCityCenter(searchGeodata, getGeodata, cityName);
      if (!center) return;
      setGeodata({
        id: center.geodataId,
        name: center.name,
        countryCode: center.countryCode,
        region: center.region,
        type: center.type,
      });
      setMapsUrl(cityMapsUrl(center.lat, center.lng));
    } finally {
      setAutofillingCity(false);
    }
  }

  const candidateLat = candidateCoords?.lat;
  const candidateLng = candidateCoords?.lng;
  useEffect(() => {
    if (candidateLat === undefined || candidateLng === undefined) return;
    let active = true;
    searchNearest({ lat: candidateLat, lng: candidateLng })
      .then((results) => active && setNearestGeodata(results))
      .catch(() => active && setNearestGeodata([]));
    return () => {
      active = false;
    };
    // searchNearest is stable for these coords; refetch only when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateLat, candidateLng]);

  const modes: ReadonlyArray<{ mode: Mode; label: string }> = [
    ...(isGeodataMiss && candidateName
      ? [{ mode: "candidate" as Mode, label: "Confirm picked venue" }]
      : []),
    { mode: "pick" as Mode, label: "Use an existing venue" },
    { mode: "maps" as Mode, label: "Create from a Google Maps link" },
    ...(item.content_type === "music_event"
      ? [
          {
            mode: "use_maps" as Mode,
            label: "Use this Google Maps link as the location (no venue)",
          },
        ]
      : []),
  ];

  // Any maps-based mode can carry a geodata override (it bypasses the distance
  // gate). It is REQUIRED on a geodata_miss (auto-resolution already failed) and
  // OPTIONAL otherwise — offered up-front so a curator who knows the auto lookup
  // will miss can pre-empt the extra round-trip. Picking an existing venue uses
  // its own geodata, so no picker there.
  const geodataApplies = mode !== "pick";
  const geodataRequired = isGeodataMiss && geodataApplies;
  const geodataOk = !geodataRequired || Boolean(geodata?.id);
  const mapsMode = mode === "maps" || mode === "use_maps";
  const mapsUrlValid = isGoogleMapsUrl(mapsUrl);
  // Show the warning only once the curator has typed something invalid.
  const mapsUrlInvalid = mapsMode && mapsUrl.trim().length > 0 && !mapsUrlValid;
  const baseOk =
    mode === "pick"
      ? Boolean(venue?.id)
      : mode === "candidate"
        ? Boolean(candidateName)
        : mapsUrlValid;
  const canSubmit = baseOk && geodataOk;

  async function handleSubmit() {
    const geodataId = geodata?.id;
    let resolution: HitlResolutionInput;
    if (mode === "pick") {
      resolution = { kind: "venue.pick", venue_id: venue!.id };
    } else if (mode === "candidate") {
      resolution = {
        kind: "venue.create_from_candidate",
        geodata_id: geodataId!,
      };
    } else if (mode === "use_maps") {
      resolution = {
        kind: "venue.use_maps_url",
        maps_url: mapsUrl.trim(),
        ...(geodataId ? { geodata_id: geodataId } : {}),
      };
    } else {
      resolution = {
        kind: "venue.create_from_maps",
        maps_url: mapsUrl.trim(),
        ...(geodataId ? { geodata_id: geodataId } : {}),
      };
    }
    setSubmitting(true);
    try {
      await resolveHitl(item.id, { resolution });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CoalescedResolveBanner item={item} />
      {extractedName && (
        <p className="text-sm text-muted-foreground">
          Extracted venue:{" "}
          <span className="font-medium text-foreground">{extractedName}</span>
        </p>
      )}

      {candidates.length > 0 && (
        <ul className="flex flex-col gap-2">
          {candidates.map((c, i) => (
            <li
              key={c.cid ?? `${c.name}-${i}`}
              className="flex items-center gap-3 rounded-md border bg-muted/20 p-2"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-foreground">
                    {c.name}
                  </span>
                  {c.inDb && (
                    <span className="rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                      In our DB
                    </span>
                  )}
                  {c.mapsUrl && (
                    <a
                      href={c.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in Google Maps (new tab)"
                      className="inline-flex items-center gap-0.5 text-xs text-blue-500 hover:underline"
                    >
                      Maps
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {c.address && (
                  <span className="truncate text-xs text-muted-foreground">
                    {c.address}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                {c.inDb && c.dbVenueId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setMode("pick");
                      setVenue({
                        id: c.dbVenueId!,
                        name: c.name,
                        address: c.address,
                      });
                    }}
                  >
                    Use this (in DB)
                  </Button>
                )}
                {c.mapsUrl && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setMode("maps");
                      setMapsUrl(c.mapsUrl!);
                    }}
                  >
                    Create from Maps
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-1">
        {modes.map(({ mode: m, label }) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={mode === m ? "default" : "outline"}
            onClick={() => setMode(m)}
          >
            {label}
          </Button>
        ))}
      </div>

      {mode === "candidate" && candidateName && (
        <p className="text-sm text-muted-foreground">
          Creating venue{" "}
          <span className="font-medium text-foreground">{candidateName}</span>{" "}
          with the geodata you pick below.
        </p>
      )}
      {mode === "pick" && (
        <RemoteSelect<VenueOption>
          value={venue}
          triggerType="change"
          onValueChanged={(selected) => setVenue(selected)}
          showSelected
          accessorFn={(option) => ({
            id: option?.id || "",
            name: option
              ? `${option.name}${option.address ? ` (${option.address})` : ""}`
              : "",
            iconUrl: option?.media?.photos?.[0]?.url,
          })}
          onSearch={async (query, onStatusChanged) => {
            onStatusChanged("loading");
            const results = (await search(query)) as VenueOption[];
            onStatusChanged("success");
            return results;
          }}
        />
      )}
      {mode === "maps" && (
        <div className="flex flex-col gap-1">
          <Input
            value={mapsUrl}
            onChange={(event) => setMapsUrl(event.target.value)}
            placeholder="https://maps.app.goo.gl/…"
          />
          <span className="text-xs text-muted-foreground">
            Short links (maps.app.goo.gl) are expanded server-side before
            lookup.
          </span>
          {mapsUrlInvalid && <MapsUrlWarning />}
        </div>
      )}
      {mode === "use_maps" && (
        <div className="flex flex-col gap-1">
          {cityName && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-fit"
              disabled={autofillingCity}
              onClick={handleAutofillCity}
            >
              {autofillingCity ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <MapPin className="mr-1 h-4 w-4" />
              )}
              Use {cityName} city center (TBA venue)
            </Button>
          )}
          <Input
            value={mapsUrl}
            onChange={(event) => setMapsUrl(event.target.value)}
            placeholder="https://maps.app.goo.gl/…"
          />
          <span className="text-xs text-muted-foreground">
            Attaches the link to the event without creating a venue entity.
          </span>
          {mapsUrlInvalid && <MapsUrlWarning />}
        </div>
      )}

      {geodataApplies && (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">
            {geodataRequired
              ? "City / geodata (required)"
              : "City / geodata (optional)"}
          </span>
          {nearestGeodata.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">
                Closest cities to the venue:
              </span>
              <div className="flex flex-wrap gap-1">
                {nearestGeodata.map((g) => (
                  <Button
                    key={g.id}
                    type="button"
                    size="sm"
                    variant={geodata?.id === g.id ? "default" : "outline"}
                    onClick={() =>
                      setGeodata({
                        id: g.id,
                        name: g.name,
                        countryCode: g.countryCode,
                        region: g.region,
                        type: g.type,
                      })
                    }
                  >
                    {formatNearestGeodataLabel(g)}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <RemoteSelect<GeodataOption>
            value={geodata}
            triggerType="change"
            showSelected
            onValueChanged={(selected) => setGeodata(selected)}
            accessorFn={(option) => ({
              id: option?.id || "",
              name: option
                ? `${option.name}${option.countryCode ? ` (${option.countryCode})` : ""}`
                : "",
            })}
            onSearch={async (query, onStatusChanged) => {
              onStatusChanged("loading");
              const results = (await searchGeodata(query)) as GeodataOption[];
              onStatusChanged("success");
              return results;
            }}
          />
          <span className="text-xs text-muted-foreground">
            {geodataRequired
              ? "The chosen geodata is used as-is — distance checks are skipped."
              : "Optional: pick a city only to override auto-detection (e.g. when it can't find one)."}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Resolve &amp; resume
        </Button>
        <span className="text-xs text-muted-foreground">
          Re-runs venue_resolution with your choice and continues to create.
        </span>
      </div>
    </div>
  );
}
